import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { PermissionGate } from '@/components/PermissionGate';
import { OrganizationSwitcher } from '@/components/OrganizationSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/Button';
import {
  LayoutDashboard,
  FileText,
  UserCog,
  Users,
  LogOut,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Calendar,
  BarChart3,
  FileStack,
  Workflow,
  Building2,
  DollarSign,
  UserCheck,
  BookOpen,
  Library,
  Mail,
  History,
  Search,
} from 'lucide-react';
import { useState, useEffect } from 'react';

export function Sidebar() {
  const { user, signOut, isAdmin } = useAuth();
  const { currentOrg, currentMember, organizations } = useOrganization();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  // Verificar se alguma rota de settings está ativa
  const isSettingsActive = () => {
    return ['/user-approvals', '/organizations', '/teams', '/members', '/users', '/settings', '/audit-log', '/email-templates', '/notifications', '/invitations'].some(
      path => location.pathname.startsWith(path)
    );
  };

  // Abrir automaticamente se estiver em uma rota de settings
  const [settingsOpen, setSettingsOpen] = useState(isSettingsActive());

  // Atualizar quando a rota mudar
  useEffect(() => {
    if (isSettingsActive()) {
      setSettingsOpen(true);
    }
  }, [location.pathname]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const navItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/dashboard',
      show: true,
    },
    {
      label: 'Contratos',
      icon: FileText,
      path: '/contracts',
      show: true,
    },
    {
      label: 'Busca Avançada',
      icon: Search,
      path: '/search',
      show: true,
    },
    {
      label: 'Minhas Aprovações',
      icon: UserCheck,
      path: '/my-approvals',
      show: true,
    },
    {
      label: 'Templates',
      icon: FileStack,
      path: '/templates',
      show: true,
    },
    {
      label: 'Cláusulas',
      icon: BookOpen,
      path: '/clauses',
      show: true,
    },
    {
      label: 'Biblioteca de Cláusulas',
      icon: Library,
      path: '/clause-templates',
      show: true,
    },
    {
      label: 'Calendário',
      icon: Calendar,
      path: '/calendar',
      show: true,
    },
    {
      label: 'Parcelas',
      icon: DollarSign,
      path: '/installments',
      show: true,
    },
    {
      label: 'Obrigações',
      icon: Calendar,
      path: '/obligations',
      show: true,
    },
    {
      label: 'Templates de Obrigações',
      icon: FileText,
      path: '/obligation-templates',
      show: true,
    },
    {
      label: 'Workflows',
      icon: Workflow,
      path: '/workflows',
      show: true,
      permission: 'manage_workflows',
    },
    {
      label: 'Relatórios',
      icon: BarChart3,
      path: '/reports',
      show: true,
    },
    {
      label: 'Relatórios Personalizados',
      icon: BarChart3,
      path: '/custom-reports',
      show: true,
    },
  ];

  const settingsSubItems = [
    {
      label: 'Aprovações',
      icon: UserCheck,
      path: '/user-approvals',
    },
    {
      label: 'Organizações',
      icon: Building2,
      path: '/organizations',
    },
    {
      label: 'Equipes',
      icon: Users,
      path: '/teams',
    },
    {
      label: 'Membros',
      icon: UserCog,
      path: '/members',
    },
    {
      label: 'Convites',
      icon: Mail,
      path: '/invitations',
      adminOnly: true,
    },
    {
      label: 'Usuários',
      icon: Users,
      path: '/users',
    },
    {
      label: 'Templates de E-mail',
      icon: Mail,
      path: '/email-templates',
      adminOnly: true,
    },
    {
      label: 'Histórico de Notificações',
      icon: History,
      path: '/notifications',
      adminOnly: true,
    },
    {
      label: 'Integrações',
      icon: Settings,
      path: '/settings',
    },
    {
      label: 'Histórico de Alterações',
      icon: History,
      path: '/audit-log',
      adminOnly: true,
    },
  ];

  return (
    <div
      className={`${
        collapsed ? 'w-20' : 'w-72'
      } bg-sidebar text-sidebar-foreground flex flex-col transition-all duration-300 border-r border-sidebar-border shadow-2xl h-screen`}
    >
      {/* Header */}
      <div className="p-6 border-b border-sidebar-border flex-shrink-0">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className={`font-bold text-2xl ${collapsed ? 'hidden' : 'block'}`}>
            <span className="bg-gradient-to-r from-primary to-chart-1 bg-clip-text text-transparent dark:text-foreground dark:bg-none">FiberX</span>
            <span className="text-primary ml-1">CLM</span>
          </Link>
          {collapsed && (
            <Link to="/dashboard" className="font-bold text-2xl mx-auto">
              <span className="bg-gradient-to-r from-primary to-chart-1 bg-clip-text text-transparent dark:text-foreground dark:bg-none">F</span>
            </Link>
          )}
        </div>

        {/* Nome da Organização */}
        {!collapsed && currentOrg && (
          <div className="mt-2">
            <p className="text-lg font-semibold text-sidebar-foreground truncate">{currentOrg.name}</p>
          </div>
        )}

        {/* Organization Switcher - apenas se houver mais de uma org */}
        {!collapsed && organizations.length > 1 && (
          <div className="mt-3 bg-sidebar-accent/30 rounded-sm p-2 border border-sidebar-border">
            <OrganizationSwitcher />
          </div>
        )}
      </div>

      {/* Navigation - COM SCROLL */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-sidebar">
        {navItems.map((item) => {
          const ItemComponent = (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-3 rounded-sm transition-all group ${
                isActive(item.path)
                  ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
              }`}
            >
              <item.icon className={`${collapsed ? 'w-6 h-6' : 'w-5 h-5'} flex-shrink-0`} />
              {!collapsed && <span className="font-medium flex-1">{item.label}</span>}
            </Link>
          );

          if (item.permission) {
            return (
              <PermissionGate key={item.path} permission={item.permission as any}>
                {ItemComponent}
              </PermissionGate>
            );
          }

          return ItemComponent;
        })}

        {/* Configurações (ADMIN ONLY) com Submenu */}
        {isAdmin && (
          <div>
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-sm transition-all ${
                isSettingsActive()
                  ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
              }`}
            >
              <Settings className={`${collapsed ? 'w-6 h-6' : 'w-5 h-5'} flex-shrink-0`} />
              {!collapsed && (
                <>
                  <span className="font-medium flex-1">Configurações</span>
                  {settingsOpen ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </>
              )}
            </button>

            {/* Submenu de Configurações */}
            {settingsOpen && !collapsed && (
              <div className="ml-4 mt-1 space-y-1 border-l-2 border-sidebar-border pl-2">
                {settingsSubItems
                  .filter((subItem) => !subItem.adminOnly || isAdmin)
                  .map((subItem) => (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      className={`flex items-center gap-3 px-3 py-2 rounded-sm transition-all text-sm ${
                        isActive(subItem.path)
                          ? 'bg-sidebar text-sidebar-foreground'
                          : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                      }`}
                    >
                      <subItem.icon className="w-4 h-4 flex-shrink-0" />
                      <span className="font-medium">{subItem.label}</span>
                    </Link>
                  ))}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-sidebar-border flex-shrink-0">
        {/* User Profile */}
        <div className="p-4">
          <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
            <div className="w-10 h-10 rounded-full bg-sidebar-primary flex items-center justify-center text-sidebar-primary-foreground font-bold text-sm flex-shrink-0">
              {currentMember?.user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{currentMember?.user?.full_name || user?.email}</p>
                <p className="text-xs text-muted-foreground capitalize truncate">{currentMember?.role || 'viewer'}</p>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSignOut}
              title="Sair"
              className="text-muted-foreground hover:text-sidebar-accent-foreground hover:bg-sidebar flex-shrink-0"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Theme Toggle */}
        <div className="px-4 pb-3">
          <ThemeToggle />
        </div>

        {/* Collapse Button */}
        <div className="p-3 border-t border-sidebar-border">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-sm text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-all"
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            {!collapsed && <span className="text-sm">Recolher</span>}
          </button>
        </div>
      </div>
    </div>
  );
}
